import React, { useRef, useEffect, useState } from "react";

export interface TableColumn {
  colname: string;
  field?: string;
  render?: (row: any, rowIndex: number) => React.ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
  children?: TableColumn[];
}

interface TableLayoutProps {
  title: string;
  actions?: React.ReactNode;
  theader?: TableColumn[];
  tdata?: any[];
  filters?: React.ReactNode;
  extra?: React.ReactNode;
  children?: React.ReactNode;
  getRowClassName?: (row: any) => string;
  onRowClick?: (row: any) => void;
}

export const TableLayout: React.FC<TableLayoutProps> = ({
  title,
  actions,
  theader,
  tdata,
  filters,
  extra,
  children,
  getRowClassName,
  onRowClick,
}) => {
  const useDataDriven =
    Array.isArray(theader) && Array.isArray(tdata) && theader.length > 0;

  const hasMultiHeaders = theader?.some((h) => h.children);

  const filtersRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const [minTableWidth, setMinTableWidth] = useState<string>("100%");

  // Sync scroll from table to filters
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const filtersContainer = filtersRef.current;

    if (!tableContainer || !filtersContainer) return;

    const handleTableScroll = () => {
      filtersContainer.scrollLeft = tableContainer.scrollLeft;
    };

    tableContainer.addEventListener("scroll", handleTableScroll);

    return () => {
      tableContainer.removeEventListener("scroll", handleTableScroll);
    };
  }, []);

  // Match table width to filter bar width
  useEffect(() => {
    const updateMinWidth = () => {
      if (filtersRef.current) {
        const filterWidth = filtersRef.current.scrollWidth;
        setMinTableWidth(`${filterWidth}px`);
      }
    };

    updateMinWidth();

    // Update on window resize
    window.addEventListener("resize", updateMinWidth);
    return () => window.removeEventListener("resize", updateMinWidth);
  }, [filters]);

  return (
    <div className="flex flex-col h-[300px] border rounded-lg overflow-hidden">
      {/* HEADER BAR */}
      <div className="bg-gray-700 text-white font-semibold p-2 flex justify-between items-center">
        <h2 className="text-lg">{title}</h2>
        <div className="flex items-center space-x-2">{actions}</div>
      </div>

      {filters && (
        <div
          ref={filtersRef}
          className="overflow-x-auto overflow-y-hidden"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`
            div[style*="scrollbarWidth"]::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {filters}
        </div>
      )}

      {/* ---------------- DATA DRIVEN MODE ---------------- */}
      {useDataDriven ? (
        <>
          <div ref={tableContainerRef} className="flex-1 overflow-auto">
            <table
              ref={tableRef}
              className="w-full"
              style={{ minWidth: minTableWidth }}
            >
              <thead className="bg-blue-500 text-white font-semibold sticky top-0 z-10">
                {/* MAIN HEADER */}
                <tr>
                  {theader!.map((th, i) => (
                    <th
                      key={i}
                      colSpan={th.colSpan}
                      rowSpan={th.rowSpan}
                      className={`
                        px-3 py-2 text-left text-sm whitespace-nowrap
                        ${th.colSpan ? "text-center border-b border-white/30" : ""}
                        ${th.className || ""}
                      `}
                    >
                      {th.colname}
                    </th>
                  ))}
                </tr>

                {/* CHILD HEADER ROW */}
                {hasMultiHeaders && (
                  <tr>
                    {theader!
                      .filter((h) => h.children)
                      .flatMap((h) => h.children!)
                      .map((child, i) => (
                        <th
                          key={`sub-${i}`}
                          className={`px-4 py-1 text-left text-sm whitespace-nowrap ${child.className || ""}`}
                        >
                          {child.colname}
                        </th>
                      ))}
                  </tr>
                )}
              </thead>

              <tbody>
                {(tdata?.length ?? 0) === 0 ? (
                  <tr>
                    <td
                      colSpan={theader?.length || 1}
                      className="text-center py-6 text-gray-500"
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  tdata!.map((row, ri) => {
                    const rowClass = getRowClassName
                      ? getRowClassName(row)
                      : "";

                    const flatHeaders = theader!.flatMap((h) =>
                      h.children ? h.children : h
                    );

                    return (
                      <tr
                        key={ri}
                        onClick={() => onRowClick?.(row)}
                        className={`bg-white hover:bg-gray-50 cursor-pointer transition-colors ${rowClass}`}
                      >
                        {flatHeaders.map((th, ci) => (
                          <td
                            key={ci}
                            className={`px-3 py-2 text-sm text-black border-b whitespace-nowrap ${th.className || ""}`}
                          >
                            {th.render
                              ? th.render(row, ri)
                              : th.field
                                ? row[th.field]
                                : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        children
      )}

      {extra && <div className="bg-gray-100 border-t px-3 py-2">{extra}</div>}
    </div>
  );
};
